<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import UniversalButton from '@/shared/components/base/UniversalButton/UniversalButton.vue';
import CircleArrow from '../icons/CircleArrow.vue';
import { UniversalButtonSizeType } from '../base/UniversalButton/types.ts';
import {
  onMounted, onUnmounted, ref,
} from 'vue';
import FadeTransition from '../transitions/FadeTransition.vue';
import { useExperiencePlayerStore } from '@/core/store/experience-player.ts';

const { t } = useI18n();

const internetAvailable = ref(typeof window !== 'undefined' ? window.navigator.onLine : true);
const visible = ref(false);

const {
  play: playExperience,
  pause: pauseExperience,
} = useExperiencePlayerStore();

const handleOnline = () => {
  internetAvailable.value = true;
  setTimeout(() => {
    visible.value = false;

    playExperience();

    window.location.reload();
  }, 300);
};

const handleOffline = () => {
  internetAvailable.value = false;
  visible.value = true;

  pauseExperience();
};

onMounted(() => {
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
});

onUnmounted(() => {
  window.removeEventListener('online', handleOnline);
  window.removeEventListener('offline', handleOffline);
});

const reload = () => {
  if (!internetAvailable.value) {
    return;
  }

  window.location.reload();
};

</script>

<template>
  <FadeTransition
    :visible="!internetAvailable"
  >
    <div
      class="no-internet-wrapper"
    >
      <Icon
        name="wifi"
        class="icon"
      />
      <div class="title">
        {{ t('core.no_internet_screen.title') }}
      </div>
      <div class="text">
        <span>{{ t('core.no_internet_screen.subtitle') }}</span>
        <a
          class="email"
          href="mailto:ohmybible@gmail.com"
        > hey@ohmybible@gmail.com</a>
      </div>
      <UniversalButton
        :title="t('button.retry')"
        :prefix-icon="CircleArrow"
        uppercase
        :height="UniversalButtonSizeType.Medium"
        class="reload-btn"
        @click="reload"
      />
    </div>
  </FadeTransition>
</template>

<style scoped>
.no-internet-wrapper {
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.6) !important;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  overflow: hidden;

  padding-left: 16px;
  padding-right: 16px;
  padding-top: calc(var(--ion-safe-area-top) + 32px);
  padding-bottom: var(--content-conteiner-bottom-padding);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.icon {
  width: 100px;
  height: 100px;
  color: rgba(248, 66, 103, 1);
  margin-bottom: 27px;
}

.title {
  color: #FFF;
  font-family: 'Poppins';
  font-size: 26px;
  font-style: normal;
  font-weight: 600;
  line-height: 34px; /* 130.769% */

  text-align: center;

  margin-bottom: 14px;
}

.text {
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Poppins';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 30px;

  text-align: center;
}

.email {
  color: rgba(255, 176, 60, 0.8);
  transition: all 0.3s ease;

  &:hover {
    color: rgba(255, 176, 60, 1);
  }
}

.reload-btn {
  margin-top: 30px;
}

.reload-btn:active  {
  box-shadow: 0px 8px 21px rgba(237, 51, 118, 1);
}
</style>
